import { useState, useCallback } from 'react';
import type { CalendarEvent, CalendarViewMode } from './types';

/**
 * Hook for managing calendar navigation (previous/next month/week)
 */
export const useCalendarNavigation = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const goToPreviousMonth = useCallback(() => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  }, [currentDate]);

  const goToNextMonth = useCallback(() => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  }, [currentDate]);

  const goToPreviousWeek = useCallback(() => {
    const prevWeek = new Date(currentDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentDate(prevWeek);
  }, [currentDate]);

  const goToNextWeek = useCallback(() => {
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentDate(nextWeek);
  }, [currentDate]);

  return {
    currentDate,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousWeek,
    goToNextWeek,
  };
};

/**
 * Hook for managing calendar view mode
 */
export const useCalendarViewMode = (initialMode: CalendarViewMode = 'monthly') => {
  const [activeViewMode, setActiveViewMode] = useState<CalendarViewMode>(initialMode);

  const handleViewModeChange = useCallback((mode: CalendarViewMode) => {
    setActiveViewMode(mode);
  }, []);

  return { activeViewMode, handleViewModeChange };
};

/**
 * Hook for getting calendar dates information
 */
export const useCalendarDates = (currentDate: Date) => {
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  return { firstDay, lastDay, daysInMonth, startingDayOfWeek };
};

/**
 * Hook for event management
 */
export const useEventManagement = (events: CalendarEvent[]) => {
  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    return events.filter(
      (event) =>
        event.date.getDate() === date.getDate() &&
        event.date.getMonth() === date.getMonth() &&
        event.date.getFullYear() === date.getFullYear()
    );
  }, [events]);

  return { getEventsForDate };
};

/**
 * Hook for date utilities
 */
export const useDateUtils = () => {
  const isDateToday = useCallback((date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  const getWeekDates = useCallback((currentDate: Date): Date[] => {
    const date = new Date(currentDate);
    const day = date.getDay();
    const diff = date.getDate() - day;
    const sundayDate = new Date(date.setDate(diff));

    const weekDates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(sundayDate);
      d.setDate(d.getDate() + i);
      weekDates.push(d);
    }
    return weekDates;
  }, []);

  return { isDateToday, getWeekDates };
};
